import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import VisitCalendar from '../components/VisitCalendar';
import CustomerVisitCalendar from '../components/CustomerVisitCalendar';
import SingleJobCalendar from '../components/SingleJobCalendar';
import axios from 'axios';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import ActiveShiftView from '../components/ActiveShiftView';
import ShohAI from '../components/ShohAI';
import { API_BASE_URL, getFileUrl } from '../config/api';
import { useNotification } from '../context/NotificationContext';

const libraries: ("places")[] = ["places"];

interface Job {
  _id: string;
  title: string;
  description: string;
  serviceType: string;
  status: string;
  location: {
    address: string;
    city: string;
    coordinates?: [number, number]; // [longitude, latitude]
  };
  schedule: {
    startDate: string;
    endDate?: string;
    preferredTime: string;
  };
  contract: {
    type: string;
    rate: number;
  };
  customer?: {
    firstName: string;
    lastName: string;
    phone: string;
    profilePhoto?: string;
    gender?: string;
    age?: number;
    weight?: number;
    preferredLanguages?: string[];
    serviceLocation?: {
      additionalDirections?: string;
    };
    medicalProfile?: {
      chronicConditions?: string[];
      otherConditions?: string;
      currentMedications?: Array<{
        name: string;
        dosage: string;
        frequency: string;
      }>;
      allergies?: string;
      dietaryRestrictions?: string;
      mobilityLevel?: string;
      bloodGroup?: string;
      specialNeeds?: string;
      medicationNotes?: string;
    };
    emergencyContact?: {
      name: string;
      phone: string;
      relationship: string;
      alternatePhone?: string;
    };
    preferredHospital?: {
      name: string;
      address: string;
      phone: string;
    };
  };
  assignedCaregiver?: {
    _id: string;
    firstName: string;
    lastName: string;
    rating: {
      average: number;
      count: number;
    };
  };
  visitSchedule?: Array<{
    _id: string;
    date: string;
    isVisitDay: boolean;
    notes?: string;
    time?: string;
    status?: string;
  }>;
  specialInstructions?: string;
  requirements?: {
    minimumExperience?: number;
    certifications?: string[];
    specializations?: string[];
    languages?: string[];
  };
}

const JobDetails: React.FC = () => {
  usePageTitle('Job Details');
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: libraries
  });
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { socket } = useNotification();
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Derive shift state
  const activeVisit = job?.visitSchedule?.find((v) => v.status === 'in-progress');

  const [safetyStatus, setSafetyStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!activeVisit || !job) return;
    const fetchSafety = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/api/shifts/${job._id}/visit/${activeVisit._id}/safety-status`, {headers: {Authorization: `Bearer ${token}`}});
            setSafetyStatus(res.data.status);
        } catch(e) { console.error('Error fetching safety status', e); }
    };
    fetchSafety();
    const int = setInterval(() => {
        if (!document.hidden) fetchSafety(); 
    }, 15000); 
    return () => clearInterval(int);
  }, [activeVisit?._id, job?._id]);

  const activeShiftData = activeVisit && job ? {
      jobId: job._id,
      visitId: activeVisit._id,
      correspondentName: user?.userType === 'customer' 
        ? `${job.assignedCaregiver?.firstName || 'Caregiver'} ${job.assignedCaregiver?.lastName || ''}`.trim()
        : `${job?.customer?.firstName || 'Customer'} ${job?.customer?.lastName || ''}`.trim(),
      correspondentPhoto: user?.userType === 'customer' 
        ? (job.assignedCaregiver as any)?.profilePhoto 
        : (job.customer as any)?.profilePhoto,
      userType: user?.userType as 'customer' | 'caregiver',
      hasActiveSafetyCheck: safetyStatus === 'pending_customer'
  } : undefined;
  
  // Find today's visit that is pending
  const todayVisit = job?.visitSchedule?.find((v) => {
    const visitDate = new Date(v.date);
    const today = new Date();
    // Compare YYYY-MM-DD strings to avoid time issues
    const isSameDay = 
      visitDate.getFullYear() === today.getFullYear() &&
      visitDate.getMonth() === today.getMonth() &&
      visitDate.getDate() === today.getDate();
      
    return (
      isSameDay &&
      v.status !== 'completed' && 
      v.status !== 'cancelled'
    );
  });

  const handleStartShift = async () => {
    if (!todayVisit || !job) return;
    
    // Check if we are already doing a shift (sanity check)
    if (activeVisit) {
      alert("A shift is already in progress.");
      return;
    }

    try {
      // DEVELOPMENT ONLY: Bypass location check
      const location = { lat: 23.8103, lng: 90.4125 }; // Dhaka coordinates as dummy
      
      const token = localStorage.getItem('token');
      await axios.post(`/api/shifts/${job._id}/shift/start`, {
        visitId: todayVisit._id,
        location
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchJobDetails();
    } catch (error) {
      console.error("Failed to start shift", error);
      alert('Failed to start shift. Please try again.');
    }
  };

  const acceptJob = async () => {
    if (!job) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/jobs/${job._id}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Job accepted successfully!');
      fetchJobDetails();
      navigate('/caregiver/jobs');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error accepting job');
    }
  };


  const handleTerminate = async () => {
    if (!job) return;
    
    // Calculate estimated refund/cost for confirmation message
    const startDate = new Date(job.schedule.startDate);
    const now = new Date();
    
    // Simple client-side estimation for the prompt
    // Actual calculation happens on server
    const oneDay = 24 * 60 * 60 * 1000;
    let daysUsed = 0;
    if (now > startDate) {
       daysUsed = Math.ceil((now.getTime() - startDate.getTime()) / oneDay);
    }
    // minimal check
    if (daysUsed < 0) daysUsed = 0;

    const confirmMsg = `Are you sure you want to terminate this contract?\n\nYou have used approximately ${daysUsed} days of service.\nThe final amount will be calculated based on days used.\n\nThis action cannot be undone.`;

    if (!window.confirm(confirmMsg)) return;

    try {
      const token = localStorage.getItem('token');
      // Prompt for reason (optional)
      const reason = window.prompt("Please provide a reason for termination (optional):");
      
      await axios.put(`/api/jobs/${job._id}/terminate`, { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Contract terminated successfully.');
      fetchJobDetails();
    } catch (error: any) {
      console.error("Failed to terminate job", error);
      alert(error.response?.data?.message || 'Failed to terminate job. Please try again.');
    }
  };

  const declineJob = async () => {
    if (!job) return;
    const reason = prompt('Please provide a reason for declining (optional):');
    try {
        const token = localStorage.getItem('token');
        await axios.post(`/api/jobs/${job._id}/decline`, { declineReason: reason || 'Not specified' }, {
            headers: { Authorization: `Bearer ${token}` }
        });
        alert('Job declined');
        navigate('/caregiver/jobs');
    } catch (error: any) {
        alert(error.response?.data?.message || 'Error declining job');
    }
  };



  useEffect(() => {
    fetchJobDetails();
  }, [id]);

  useEffect(() => {
    if (!socket || !id) return;
    socket.emit('join_job', id);
    
    socket.on('shift_started', () => fetchJobDetails());
    socket.on('shift_ended', () => fetchJobDetails());
    
    return () => {
        socket.emit('leave_job', id);
        socket.off('shift_started');
        socket.off('shift_ended');
    };
  }, [socket, id]);

  const fetchJobDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/jobs/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setJob(response.data.data);
      setLoading(false);
    } catch (error: any) {
      console.error('Error fetching job details:', error);
      setLoading(false);
    }
  };

  const isShiftStartable = (timeString: string) => {
    if (!timeString) return true;
    try {
      const parts = timeString.split('-');
      const startStr = parts[0].trim();
      const endStr = parts[1] ? parts[1].trim() : null;

      const parseTimeDate = (str: string) => {
         let [time, period] = str.split(' ');
         let h, m;
         
         if (!period) {
            // "HH:MM" or "HH"
            const tMap = time.split(':').map(Number);
            h = tMap[0];
            m = tMap[1] || 0;
         } else {
             // "HH:MM PM"
             const tMap = time.split(':').map(Number);
             h = tMap[0];
             m = tMap[1] || 0;
             if (period === 'PM' && h !== 12) h += 12;
             if (period === 'AM' && h === 12) h = 0;
         }
         
         const d = new Date();
         d.setHours(h, m, 0, 0);
         return d;
      };

      const now = new Date();
      const shiftStart = parseTimeDate(startStr);
      let shiftEnd = endStr ? parseTimeDate(endStr) : null;
      
      // Handle overnight shift assumption
      if (shiftEnd && shiftEnd < shiftStart) {
          shiftEnd.setDate(shiftEnd.getDate() + 1);
      }

      // Check window
      if (now < shiftStart) return false;
      if (shiftEnd && now > shiftEnd) return false;
      
      return true;
    } catch (e) {
      console.error("Error parsing shift time", e);
      return true; // Fallback to allow start if time format is unexpected
    }
  };

  const getServiceBadgeColor = (type: string): string => {
    const colors: Record<string, string> = {
      'nurse-care': 'bg-red-100 text-red-800',
      'everyday-assistance': 'bg-primary-100 text-primary-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Job not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pb-20 sm:pb-0 animate-fade-in">
      {/* Header */}
      
      {/* Content */}
      <div className="flex-grow w-full px-4 py-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Back Button */}
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors mb-6 group"
          >
            <div className="w-8 h-8 rounded-full bg-white shadow-sm group-hover:bg-white/80 flex items-center justify-center mr-3 transition-colors">
              <svg className="w-5 h-5 text-gray-600 group-hover:text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </div>
            <span className="font-medium">Back</span>
          </button>

          {/* Job Proposal Action Box */}
          {job && job.status === 'pending' && user?.userType === 'caregiver' && (
            <div className="bg-white border border-blue-100 rounded-2xl shadow-lg p-6 mb-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -mr-10 -mt-10 opacity-50 pointer-events-none"></div>
                
                <div className="relative z-10">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                </span>
                                New Job Proposal
                            </h2>
                            <p className="text-gray-600 mt-1 max-w-xl">
                                You have successfully received this job request. Please review the details below and accept or decline.
                            </p>
                        </div>
                        <div className="flex gap-3 w-full md:w-auto">
                            <button
                                onClick={declineJob}
                                className="flex-1 md:flex-none px-6 py-2.5 bg-white border border-red-200 text-red-600 font-medium rounded-xl hover:bg-red-50 transition-colors shadow-sm"
                            >
                                Decline/Skip
                            </button>
                            <button
                                onClick={acceptJob}
                                className="flex-1 md:flex-none px-8 py-2.5 bg-primary-600 text-white font-bold rounded-xl hover:bg-primary-700 transition-all shadow-md hover:shadow-lg transform active:scale-95"
                            >
                                Accept Job
                            </button>
                        </div>
                    </div>
                </div>
            </div>
          )}

          {/* Start Shift Call-to-Action */}
          {!activeVisit && todayVisit && user?.userType === 'caregiver' && todayVisit.status === 'pending' && job && (
            <div className={`bg-primary-50 border border-primary-200 rounded-2xl shadow-sm p-6 mb-6 flex flex-col md:flex-row items-center justify-between transition-opacity duration-300 ${!isShiftStartable(job.schedule.preferredTime) ? 'opacity-80' : ''}`}>
              <div className="mb-4 md:mb-0">
                <h3 className="text-xl font-bold text-gray-900 flex items-center">
                  <span className="bg-primary-100 text-primary-700 p-2 rounded-full mr-3">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </span>
                  Ready to start today's shift?
                </h3>
                <p className="text-gray-600 mt-1 ml-12">
                  Scheduled for {new Date(todayVisit.date).toLocaleDateString()}
                  <span className="ml-2 font-mono bg-white px-2 py-0.5 rounded border border-gray-200 text-sm">
                    {job.schedule.preferredTime}
                  </span>
                </p>
              </div>
              
              {isShiftStartable(job.schedule.preferredTime) ? (
                <button
                  onClick={handleStartShift}
                  className="w-full md:w-auto px-8 py-3 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl shadow-md transform transition hover:scale-105 flex items-center justify-center animate-pulse"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Start Shift
                </button>
              ) : (
                 <div className="flex flex-col items-end">
                    <button
                      disabled
                      className="w-full md:w-auto px-8 py-3 bg-gray-300 text-gray-500 font-bold rounded-xl cursor-not-allowed flex items-center justify-center"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Starts Soon
                    </button>
                    <p className="text-xs text-primary-600 font-medium mt-2">
                        You can start at {job.schedule.preferredTime.split('-')[0]}
                    </p>
                 </div>
              )}
            </div>
          )}

          {/* Active Shift View */}
          {activeVisit && (
             <div className="mb-6">
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-green-100">
                   <div className="bg-green-50 px-6 py-4 border-b border-green-100 flex justify-between items-center">
                      <span className="flex items-center gap-3 font-bold text-lg text-green-800">
                          <span className="relative flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                          </span>
                          Active Shift in Progress
                      </span>
                   </div>
                   <div className="p-6">
                      <ActiveShiftView 
                        job={job} 
                        visit={activeVisit} 
                        userType={user?.userType as 'caregiver' | 'customer'} 
                        onShiftEnd={() => window.location.reload()} 
                      />
                   </div>
                </div>
             </div>
          )}

          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          {/* Header */}
          <div className="flex justify-between items-start mb-6 pb-6 border-b border-gray-200">
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">{job.title}</h1>
              <div className="flex flex-wrap gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${getServiceBadgeColor(job.serviceType)}`}>
                  {job.serviceType.replace('-', ' ')}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${
                  job.status === 'completed' ? 'bg-green-100 text-green-800' :
                  job.status === 'in-progress' ? 'bg-primary-100 text-primary-700' :
                  job.status === 'open' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {job.status}
                </span>
              </div>
            </div>
          </div>

          {/* Patient Information */}
          {job.customer && (
            <div className="mb-6 pb-6 border-b border-gray-200">
               <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  About the Patient
               </h2>
               <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 flex flex-col md:flex-row md:items-center gap-4">
                   <div className="flex-shrink-0">
                      {job.customer.profilePhoto ? (
                          <img src={getFileUrl(job.customer.profilePhoto)} alt="Patient" className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm" />
                       ) : (
                          <div className="w-16 h-16 rounded-full bg-blue-200 flex items-center justify-center text-blue-500">
                             <span className="text-2xl font-bold">{job.customer.firstName?.[0]}</span>
                          </div>
                       )}
                   </div>
                   
                   <div className="flex-grow">
                      <h3 className="text-xl font-bold text-gray-900">
                          {job.customer.firstName} {job.customer.lastName}
                      </h3>
                      <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-700">
                          {job.customer.gender && (
                             <span className="flex items-center px-3 py-1 bg-white rounded-full shadow-sm border border-blue-100">
                                <span className="text-gray-500 mr-2">Gender:</span>
                                <span className="font-semibold capitalize">{job.customer.gender}</span>
                             </span>
                          )}
                          {job.customer.age && (
                             <span className="flex items-center px-3 py-1 bg-white rounded-full shadow-sm border border-blue-100">
                                <span className="text-gray-500 mr-2">Age:</span>
                                <span className="font-semibold">{job.customer.age} years</span>
                             </span>
                          )}
                          {job.customer.weight && (
                             <span className="flex items-center px-3 py-1 bg-white rounded-full shadow-sm border border-blue-100">
                                <span className="text-gray-500 mr-2">Weight:</span>
                                <span className="font-semibold">{job.customer.weight} kg</span>
                             </span>
                          )}
                          {job.customer.phone && (
                             <span className="flex items-center px-3 py-1 bg-white rounded-full shadow-sm border border-blue-100">
                                <span className="text-gray-500 mr-2">Phone:</span>
                                <a href={`tel:${job.customer.phone}`} className="font-semibold text-blue-600 hover:text-blue-800 transition-colors">{job.customer.phone}</a>
                             </span>
                          )}
                          {job.customer.preferredLanguages && job.customer.preferredLanguages.length > 0 && (
                            <span className="flex items-center px-3 py-1 bg-white rounded-full shadow-sm border border-blue-100">
                                <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                </svg>
                                <span className="text-gray-500 mr-1">Speaks:</span>
                                <span className="font-semibold">{job.customer.preferredLanguages.join(', ')}</span>
                            </span>
                          )}
                      </div>
                   </div>
               </div>
            </div>
          )}

          {/* Description */}
          <div className="mb-6 pb-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Job Description
            </h2>
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                {job.description ? job.description.replace(/\n\n---------- PATIENT DETAILS ----------[\s\S]*$/, '') : 'No description provided.'}
              </p>
            </div>
          </div>

          {/* Schedule */}
          <div className="mb-6 pb-6 border-b border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Schedule
              </h2>
              <div className="space-y-3 text-sm">
                <div className="flex items-center text-gray-700 bg-gray-50 p-3 rounded-lg">
                  <svg className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <div>
                    <p className="font-semibold text-gray-900">Start Date</p>
                    <p className="text-gray-600">{new Date(job.schedule.startDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                </div>
                {job.schedule.endDate && (
                  <div className="flex items-center text-gray-700 bg-gray-50 p-3 rounded-lg">
                    <svg className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <div>
                      <p className="font-semibold text-gray-900">End Date</p>
                      <p className="text-gray-600">{new Date(job.schedule.endDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center text-gray-700 bg-gray-50 p-3 rounded-lg">
                  <svg className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-semibold text-gray-900">Working Hours</p>
                    <p className="text-gray-600">{job.schedule.preferredTime}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="w-5 h-5 mr-2 text-primary-600 flex items-center justify-center text-xl font-bold">৳</span>
                Payment Details
              </h2>
              <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-6 border-2 border-primary-200">
                <p className="text-4xl font-bold text-primary-700 mb-2">
                  ৳{job.contract.rate.toLocaleString()}
                </p>
                <p className="text-base text-primary-900 font-medium mb-1">One-time Contract</p>
                <div className="mt-3 pt-3 border-t border-primary-200">
                  <p className="text-sm text-primary-800">• 8 hours per day</p>
                  <p className="text-sm text-primary-800 mt-1">• {job.contract.type === 'weekly' ? '7 days' : '30 days'} contract</p>
                </div>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="mb-6 pb-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Service Location
            </h2>
            <div className="flex items-start text-gray-700 bg-gray-50 p-4 rounded-lg mb-4">
              <svg className="w-6 h-6 mr-3 text-primary-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div>
                <p className="font-semibold text-gray-900 mb-1">Address</p>
                <p className="text-gray-700">{job.location.address}</p>
                <p className="text-gray-600">{job.location.city}</p>
                {job.customer && job.customer.serviceLocation && job.customer.serviceLocation.additionalDirections && (
                  <div className="mt-2 bg-yellow-50 p-2 rounded border border-yellow-200 text-sm">
                    <span className="font-bold text-yellow-800 block text-xs uppercase">Directions:</span>
                    <span className="text-gray-800">{job.customer.serviceLocation.additionalDirections}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Show map only to caregivers with coordinates */}
            {user?.userType === 'caregiver' && job.location.coordinates && isLoaded && (
              <div className="mt-4">
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '300px', borderRadius: '8px' }}
                    center={{
                      lat: job.location.coordinates[1],
                      lng: job.location.coordinates[0]
                    }}
                    zoom={15}
                    options={{
                      streetViewControl: false,
                      mapTypeControl: false,
                    }}
                  >
                    <Marker
                      position={{
                        lat: job.location.coordinates[1],
                        lng: job.location.coordinates[0]
                      }}
                    />
                  </GoogleMap>
                <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex gap-2 text-sm text-blue-800">
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p>This is the precise location where you'll provide care services. The customer's exact address is shown above.</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Medical Profile (Crucial for Caregivers) */}
          {user?.userType === 'caregiver' && job.customer && job.customer.medicalProfile && (
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                Patient Medical Profile
              </h2>
              <div className="bg-red-50 rounded-lg p-4 border border-red-100 space-y-3">
                
                {/* Conditions */}
                {((job.customer.medicalProfile.chronicConditions && job.customer.medicalProfile.chronicConditions.length > 0) || job.customer.medicalProfile.otherConditions) && (
                  <div>
                    <h3 className="text-sm font-bold text-red-800 uppercase tracking-wide mb-1">Medical Conditions</h3>
                    <div className="flex flex-wrap gap-2">
                        {job.customer.medicalProfile.chronicConditions?.map((cond, i) => (
                            <span key={i} className="px-2 py-1 bg-white text-red-700 text-xs rounded border border-red-200 capitalize">
                                {cond.replace('-', ' ')}
                            </span>
                        ))}
                        {job.customer.medicalProfile.otherConditions && (
                           <span className="px-2 py-1 bg-white text-gray-700 text-xs rounded border border-gray-200">
                                {job.customer.medicalProfile.otherConditions}
                           </span>
                        )}
                    </div>
                  </div>
                )}

                {/* Medications */}
                {job.customer.medicalProfile.currentMedications && job.customer.medicalProfile.currentMedications.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-red-800 uppercase tracking-wide mb-1">Medications</h3>
                     <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {job.customer.medicalProfile.currentMedications.map((med, i) => (
                          <li key={i} className="text-sm bg-white p-2 rounded border border-red-100 text-gray-800">
                             <strong>{med.name}</strong> 
                             {med.dosage && <span className="text-gray-500 mx-1">• {med.dosage}</span>}
                             {med.frequency && <span className="text-gray-500 italic">({med.frequency})</span>}
                          </li>
                        ))}
                     </ul>
                  </div>
                )}

                {/* Other Info */}
                <div className="grid grid-cols-2 gap-4 pt-2">
                   {job.customer.medicalProfile.bloodGroup && (
                       <div>
                         <span className="text-xs text-gray-500 block">Blood Group</span>
                         <span className="font-semibold text-gray-900">{job.customer.medicalProfile.bloodGroup}</span>
                       </div>
                   )}
                   {job.customer.medicalProfile.mobilityLevel && (
                       <div>
                         <span className="text-xs text-gray-500 block">Mobility</span>
                         <span className="font-semibold text-gray-900 capitalize">{job.customer.medicalProfile.mobilityLevel.replace('-', ' ')}</span>
                       </div>
                   )}
                   {job.customer.medicalProfile.allergies && (
                       <div className="col-span-2">
                         <span className="text-xs text-gray-500 block">Allergies</span>
                         <span className="text-red-700 font-medium">{job.customer.medicalProfile.allergies}</span>
                       </div>
                   )}
                   {job.customer.medicalProfile.dietaryRestrictions && (
                       <div className="col-span-2">
                         <span className="text-xs text-gray-500 block">Dietary Restrictions</span>
                         <span className="text-gray-800">{job.customer.medicalProfile.dietaryRestrictions}</span>
                       </div>
                   )}
                   {job.customer.medicalProfile.specialNeeds && (
                       <div className="col-span-2 bg-yellow-50 p-2 rounded border border-yellow-100">
                         <span className="text-xs text-yellow-800 font-bold block uppercase tracking-wide">Special Needs</span>
                         <span className="text-gray-800 text-sm">{job.customer.medicalProfile.specialNeeds}</span>
                       </div>
                   )}
                   {job.customer.medicalProfile.medicationNotes && (
                       <div className="col-span-2 bg-blue-50 p-2 rounded border border-blue-100">
                         <span className="text-xs text-blue-800 font-bold block uppercase tracking-wide">Medication Notes</span>
                         <span className="text-gray-800 text-sm">{job.customer.medicalProfile.medicationNotes}</span>
                       </div>
                   )}
                </div>

              </div>
            </div>
          )}

          {/* Emergency & Hospital Info (Caregiver View) */}
          {user?.userType === 'caregiver' && job.customer && (job.customer.emergencyContact || job.customer.preferredHospital) && (
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Emergency Information
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {/* Emergency Contact */}
                {job.customer.emergencyContact && (
                  <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                    <h3 className="text-xs font-bold text-orange-800 uppercase tracking-wide mb-3 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      Primary Emergency Contact
                    </h3>
                    <div className="space-y-2">
                      <div>
                        <p className="text-sm text-gray-500">Name</p>
                        <p className="font-semibold text-gray-900">{job.customer.emergencyContact.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Relationship</p>
                        <p className="font-medium text-gray-900">{job.customer.emergencyContact.relationship}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Phone</p>
                        <a href={`tel:${job.customer.emergencyContact.phone}`} className="font-bold text-primary-600 hover:underline flex items-center">
                            {job.customer.emergencyContact.phone}
                        </a>
                      </div>
                      {job.customer.emergencyContact.alternatePhone && (
                        <div>
                            <p className="text-sm text-gray-500">Alternate Phone</p>
                            <a href={`tel:${job.customer.emergencyContact.alternatePhone}`} className="font-medium text-gray-700 hover:underline">
                                {job.customer.emergencyContact.alternatePhone}
                            </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Preferred Hospital */}
                {job.customer.preferredHospital && (
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                    <h3 className="text-xs font-bold text-blue-800 uppercase tracking-wide mb-3 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        Preferred Hospital
                    </h3>
                    <div className="space-y-2">
                        <div>
                            <p className="text-sm text-gray-500">Hospital Name</p>
                            <p className="font-semibold text-gray-900">{job.customer.preferredHospital.name}</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Address</p>
                            <p className="font-medium text-gray-900 text-sm">{job.customer.preferredHospital.address}</p>
                        </div>
                        {job.customer.preferredHospital.phone && (
                            <div>
                                <p className="text-sm text-gray-500">Contact</p>
                                <a href={`tel:${job.customer.preferredHospital.phone}`} className="font-medium text-blue-600 hover:underline">
                                    {job.customer.preferredHospital.phone}
                                </a>
                            </div>
                        )}
                        <div className="mt-2 pt-2 border-t border-blue-100">
                             <a 
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${job.customer.preferredHospital.name} ${job.customer.preferredHospital.address}`)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs font-bold text-blue-700 hover:text-blue-900 flex items-center"
                             >
                                 Get Directions &rarr;
                             </a>
                        </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Single Job Calendar (for caregiver view - active/in-progress jobs only) */}
          {user?.userType === 'caregiver' && (job.status === 'accepted' || job.status === 'in-progress') && job.schedule.startDate && job.schedule.endDate && (
            <div className="mb-6">
              <SingleJobCalendar
                jobTitle={job.title}
                customerName={`${job.customer?.firstName || ''} ${job.customer?.lastName || ''}`}
                startDate={job.schedule.startDate}
                endDate={job.schedule.endDate}
                visitSchedule={job.visitSchedule}
                contractRate={job.contract.rate}
                contractType={job.contract.type}
              />
            </div>
          )}

          {/* Caregiver Info (for customer view) */}
          {user?.userType === 'customer' && job.assignedCaregiver && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Assigned Caregiver</h2>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-medium text-gray-900">
                  {job.assignedCaregiver.firstName} {job.assignedCaregiver.lastName}
                </p>
                {job.assignedCaregiver.rating && job.assignedCaregiver.rating.count > 0 && (
                  <div className="flex items-center mt-2">
                    <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span className="ml-1 text-sm text-gray-600">
                      {job.assignedCaregiver.rating.average.toFixed(1)} ({job.assignedCaregiver.rating.count} reviews)
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Visit Calendar - Customer View (with editing) */}
          {user?.userType === 'customer' && (job.status === 'accepted' || job.status === 'in-progress') && job.schedule.startDate && job.schedule.endDate && job.assignedCaregiver && (
            <div className="mb-6">
              <CustomerVisitCalendar
                jobId={job._id}
                startDate={new Date(job.schedule.startDate)}
                endDate={new Date(job.schedule.endDate)}
                visitSchedule={(job.visitSchedule || []).map(vs => ({
                  date: new Date(vs.date),
                  isVisitDay: vs.isVisitDay,
                  notes: vs.notes,
                  time: vs.time
                }))}
                specialInstructions={job.specialInstructions}
                caregiverId={job.assignedCaregiver._id}
                onScheduleUpdate={fetchJobDetails}
                currentPreferredTime={job.schedule.preferredTime}
              />
            </div>
          )}

          {/* Terminate Contract Button (Customer) */}
          {user?.userType === 'customer' && (job.status === 'accepted' || job.status === 'in-progress') && (
            <div className="mb-8 p-4 border border-red-200 rounded-lg bg-red-50">
              <h3 className="text-lg font-bold text-red-700 mb-2">Contract Management</h3>
              <p className="text-sm text-gray-700 mb-4">
                If you need to end this contract early, you can terminate it here. 
                You will only be charged for the days used. The caregiver will be released immediately.
              </p>
              <button
                onClick={handleTerminate}
                className="w-full sm:w-auto px-6 py-2 bg-red-600 text-white font-semibold rounded hover:bg-red-700 transition-colors shadow-sm flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Terminate Contract
              </button>
            </div>
          )}

          {/* Termination Details (if terminated) */}
          {job.status === 'terminated' && (job as any).terminationDetails && (
             <div className="mb-8 p-6 border border-gray-300 rounded-xl bg-gray-50">
                <div className="flex items-center gap-3 mb-4">
                   <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                     <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                     </svg>
                   </div>
                   <div>
                      <h3 className="text-xl font-bold text-gray-800">Contract Terminated</h3>
                      <p className="text-sm text-gray-500">This contract was ended early.</p>
                   </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                   <div className="bg-white p-3 rounded border border-gray-200">
                      <p className="text-gray-500">Termination Date</p>
                      <p className="font-semibold">{new Date((job as any).terminationDetails.terminatedAt).toLocaleDateString()}</p>
                   </div>
                   <div className="bg-white p-3 rounded border border-gray-200">
                      <p className="text-gray-500">Reason</p>
                      <p className="font-semibold">{(job as any).terminationDetails.reason || 'No reason provided'}</p>
                   </div>
                   <div className="bg-white p-3 rounded border border-gray-200">
                      <p className="text-gray-500">Days Used</p>
                      <p className="font-semibold">{(job as any).terminationDetails.daysUsed} days</p>
                   </div>
                   <div className="bg-white p-3 rounded border border-gray-200">
                      <p className="text-gray-500">Final Amount Charged</p>
                      <p className="font-semibold text-lg text-primary-600">৳ {(job as any).terminationDetails.finalAmount?.toLocaleString()}</p>
                   </div>
                </div>
             </div>
          )}


        </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="hidden sm:block bg-gray-900 text-white py-8 px-4 mt-auto">
        <div className="max-w-7xl mx-auto text-center">
          <h4 className="text-2xl font-bold font-proyas tracking-wider mb-2">PROYAS</h4>
          <p className="text-gray-400 text-sm">Quality care, delivered with compassion</p>
          
          {/* Contact Info */}
          <div className="flex justify-center items-center gap-6 mt-4">
            <a href="mailto:contact@proyas.net" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
              </svg>
              <span className="text-sm">contact@proyas.net</span>
            </a>
            <a href="https://www.facebook.com/profile.php?id=61586633764841" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <span className="text-sm">Facebook</span>
            </a>
          </div>
          
          <p className="text-gray-500 text-xs mt-4">© 2026 <span className="font-proyas tracking-wider">PROYAS</span>. All rights reserved.</p>
        </div>
      </footer>
      <ShohAI activeShift={activeShiftData} />
    </div>
  );
};

export default JobDetails;
